import { MAPS, type MapDef } from "@neon-purge/shared";
import { el } from "./dom.js";
import { drawMapPreview } from "./mapPreview.js";

/**
 * DESKTOP "damier": a left-side grid of clickable map squares — a fast
 * alternative to the ‹ › arrow picker, which stays intact. One square per map
 * valid for the current mode (thumbnail via the cheap 2D `drawMapPreview`, no
 * WebGL per square), plus a trailing 🎲 random square. Used by both the
 * play screen (local pick) and the room lobby (server pick, owner-only).
 *
 * Task #3 will insert a "custom" square after 🎲 that opens the published-maps
 * browser — the render loop here is the place to add it.
 */

const RANDOM_ID = "__random__";

export interface MapGridHandle {
  root: HTMLElement;
  /** Re-filter + rebuild squares for a new mode. */
  setMode(mode: string): void;
  /** Highlight the square for the active map id (or __random__). */
  setActive(mapId: string): void;
}

export function mapGrid(opts: {
  mode: string;
  activeId: string;
  /** false → read-only (non-owner in the lobby): no click, dimmed. */
  interactive?: boolean;
  onPick: (mapId: string) => void;
  /** If given, a "🎨 Custom" square is added after 🎲 Random that opens the
   *  community published-maps browser instead of selecting a built-in map. */
  onCustom?: () => void;
}): MapGridHandle {
  const grid = el("div", { class: "map-grid" });
  const root = el("div", { class: "card map-grid-card" },
    el("div", { class: "card-title", text: "MAPS" }), grid);
  let mode = opts.mode;
  let activeId = opts.activeId;
  const interactive = opts.interactive !== false;
  const cells = new Map<string, HTMLElement>();

  const cell = (id: string, name: string, m: MapDef | null): HTMLElement => {
    const thumb = m
      ? (() => {
          const cv = document.createElement("canvas");
          cv.width = 128; cv.height = 96; cv.className = "map-cell-thumb";
          drawMapPreview(cv, m);
          return cv;
        })()
      : el("div", { class: "map-cell-thumb map-cell-random", text: "🎲" });
    const c = el("div", { class: "map-cell", title: name }, thumb,
      el("div", { class: "map-cell-name", text: name.replace("🎲 ", "") }));
    if (interactive) {
      c.classList.add("clickable");
      c.onclick = () => opts.onPick(id);
    }
    cells.set(id, c);
    return c;
  };

  const highlight = (): void => {
    for (const [id, c] of cells) c.classList.toggle("active", id === activeId);
  };

  const build = (): void => {
    cells.clear();
    const squares = Object.values(MAPS)
      .filter((m) => m.modes.includes(mode))
      // Mega Chibre pinned LAST among the maps, so it sits right before 🎲 Random.
      .sort((a, b) => (a.id === "mega_chibre" ? 1 : b.id === "mega_chibre" ? -1 : 0))
      .map((m) => cell(m.id, m.name, m));
    squares.push(cell(RANDOM_ID, "🎲 Random", null));
    if (opts.onCustom) {
      const c = el("div", { class: "map-cell clickable", title: "Community maps" },
        el("div", { class: "map-cell-thumb map-cell-random", text: "🎨" }),
        el("div", { class: "map-cell-name", text: "Custom" }));
      c.onclick = () => opts.onCustom!();
      squares.push(c);
    }
    grid.replaceChildren(...squares);
    highlight();
  };

  build();
  return {
    root,
    setMode(m: string): void { if (m !== mode) { mode = m; build(); } },
    setActive(id: string): void { activeId = id; highlight(); },
  };
}
